import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type EntityAgreementAccessJSON = z.infer<typeof EntityAgreementAccessSchema.EntityAgreementAccessJSONZ>;

export type EntityAgreementAccessParams = z.infer<typeof EntityAgreementAccessSchema.EntityAgreementAccessZ>;

export default class EntityAgreementAccessSchema {
  public static readonly EntityAgreementAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.EntityAgreementAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    entityAgreementId: z.uuid().nullish(),
  });

  public static readonly EntityAgreementAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.EntityAgreementAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    entityAgreementId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): EntityAgreementAccessParams {
    return parseOrThrow(EntityAgreementAccessSchema.EntityAgreementAccessZ, values, {
      logError: true,
      schemaName: "EntityAgreementAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): EntityAgreementAccessJSON {
    return parseOrThrow(EntityAgreementAccessSchema.EntityAgreementAccessJSONZ, values, {
      logError: true,
      schemaName: "EntityAgreementAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is EntityAgreementAccessParams {
    try {
      EntityAgreementAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is EntityAgreementAccessJSON {
    try {
      EntityAgreementAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: EntityAgreementAccessJSON): EntityAgreementAccessParams {
    const parsed = parseOrThrow(EntityAgreementAccessSchema.EntityAgreementAccessJSONZ, data, {
      logError: true,
      schemaName: "EntityAgreementAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return EntityAgreementAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: EntityAgreementAccessParams): EntityAgreementAccessJSON {
    const parsed = EntityAgreementAccessSchema.validate(notification);
    return EntityAgreementAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    EntityAgreementAccessSchema.EntityAgreementAccessJSONZ,
    EntityAgreementAccessSchema.EntityAgreementAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.EntityAgreementAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        entityAgreementId: Guid.fromJSON(data.entityAgreementId) ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.EntityAgreementAccess as const,
        accessLevel: resource.accessLevel,
        entityAgreementId: resource.entityAgreementId?.toJSON(),
      }),
    }
  );
}
